// Room settings management utilities

// Default room configuration (fallback)
const DEFAULT_ROOMS = [
  { roomNumber: "101", capacity: 50, id: "101" },
  { roomNumber: "102", capacity: 50, id: "102" },
  { roomNumber: "103", capacity: 50, id: "103" },
  { roomNumber: "217", capacity: 50, id: "217" },
  { roomNumber: "301", capacity: 45, id: "301" },
  { roomNumber: "302", capacity: 40, id: "302" },
  { roomNumber: "303", capacity: 40, id: "303" },
  { roomNumber: "304", capacity: 45, id: "304" },
  { roomNumber: "305", capacity: 45, id: "305" },
  { roomNumber: "306", capacity: 48, id: "306" },
  { roomNumber: "307", capacity: 48, id: "307" },
  { roomNumber: "308", capacity: 45, id: "308" },
  { roomNumber: "401", capacity: 38, id: "401" },
  { roomNumber: "402", capacity: 38, id: "402" },
  { roomNumber: "403", capacity: 40, id: "403" },
  { roomNumber: "403A", capacity: 40, id: "403A" },
  { roomNumber: "405", capacity: 40, id: "405" },
  { roomNumber: "406", capacity: 40, id: "406" },
  { roomNumber: "408", capacity: 45, id: "408" },
  { roomNumber: "409", capacity: 45, id: "409" },
];

const STORAGE_KEY = "room_allocation_settings";

/**
 * Load room settings from localStorage or return defaults
 */
export const loadRoomSettings = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate the structure
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map((room) => ({
          roomNumber: room.roomNumber?.toString() || "",
          capacity: parseInt(room.capacity) || 0,
          id: room.id || room.roomNumber?.toString() || "",
        }));
      }
    }
  } catch (error) {
    console.warn("Failed to load room settings from localStorage:", error);
  }

  return [...DEFAULT_ROOMS];
};

/**
 * Save room settings to localStorage
 */
export const saveRoomSettings = (roomSettings) => {
  try {
    const settingsToSave = roomSettings.map((room) => ({
      roomNumber: room.roomNumber.toString(),
      capacity: parseInt(room.capacity),
      id: room.id || room.roomNumber.toString(),
    }));

    localStorage.setItem(STORAGE_KEY, JSON.stringify(settingsToSave));
    console.log("Room settings saved successfully");
    return true;
  } catch (error) {
    console.error("Failed to save room settings:", error);
    return false;
  }
};

/**
 * Convert room settings array to the format expected by roomAllocation.js
 * Returns both AVAILABLE_ROOMS array and ROOM_CAPACITY object
 */
export const convertToAllocationFormat = (roomSettings) => {
  const availableRooms = roomSettings.map((room) => room.roomNumber);
  const roomCapacity = {};

  roomSettings.forEach((room) => {
    // Handle both string and numeric keys for compatibility
    const key = isNaN(room.roomNumber) ? room.roomNumber : parseInt(room.roomNumber);
    roomCapacity[key] = parseInt(room.capacity);
  });

  return {
    availableRooms,
    roomCapacity,
  };
};

/**
 * Reset room settings to defaults
 */
export const resetToDefaults = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return [...DEFAULT_ROOMS];
  } catch (error) {
    console.error("Failed to reset room settings:", error);
    return [...DEFAULT_ROOMS];
  }
};

/**
 * Validate room settings structure
 */
export const validateRoomSettings = (roomSettings) => {
  if (!Array.isArray(roomSettings)) {
    return { isValid: false, error: "Room settings must be an array" };
  }

  for (let i = 0; i < roomSettings.length; i++) {
    const room = roomSettings[i];

    if (!room.roomNumber || typeof room.roomNumber !== "string") {
      return { isValid: false, error: `Room ${i + 1}: Invalid room number` };
    }

    if (!room.capacity || typeof room.capacity !== "number" || room.capacity <= 0) {
      return { isValid: false, error: `Room ${room.roomNumber}: Invalid capacity` };
    }

    if (room.capacity > 500) {
      return { isValid: false, error: `Room ${room.roomNumber}: Capacity too high (max 500)` };
    }
  }

  // Check for duplicate room numbers
  const roomNumbers = roomSettings.map((room) => room.roomNumber);
  const duplicates = roomNumbers.filter((room, index) => roomNumbers.indexOf(room) !== index);

  if (duplicates.length > 0) {
    return { isValid: false, error: `Duplicate room numbers found: ${duplicates.join(", ")}` };
  }

  return { isValid: true };
};

/**
 * Get room statistics
 */
export const getRoomStatistics = (roomSettings) => {
  if (!Array.isArray(roomSettings) || roomSettings.length === 0) {
    return {
      totalRooms: 0,
      totalCapacity: 0,
      averageCapacity: 0,
      minCapacity: 0,
      maxCapacity: 0,
    };
  }

  const capacities = roomSettings.map((room) => room.capacity);
  const totalCapacity = capacities.reduce((sum, capacity) => sum + capacity, 0);

  return {
    totalRooms: roomSettings.length,
    totalCapacity,
    averageCapacity: Math.round(totalCapacity / roomSettings.length),
    minCapacity: Math.min(...capacities),
    maxCapacity: Math.max(...capacities),
  };
};

const roomSettingsApi = {
  loadRoomSettings,
  saveRoomSettings,
  convertToAllocationFormat,
  resetToDefaults,
  validateRoomSettings,
  getRoomStatistics,
  DEFAULT_ROOMS,
};

export default roomSettingsApi;
